import moment from 'moment';
import StorageService from './StorageService';
import {isArray} from "rxjs/internal/util/isArray";
import { get } from "./SimpleClient";
import { storeSettings } from "./ApiTasks";

const storage = StorageService.getInstance();

/**
 * Add to a stored array.
 *
 * @param {string} arr   The array.
 * @param {string} value The value.
 */
export const addToStoredArray = async (arr, value) => {
  let element = {
    ref: Math.random().toString(36).substr(2, 9),
    added: moment().format('YYYY-MM-DD HH:mm:ss'),
    value,
  };

  let stackObj = [];

  let stack = await storage.get({key: arr});
  if (stack.value && stack.value.length > 0) {
    stackObj = JSON.parse(stack.value);
  }

  stackObj.push(element);

  await storage.set({key: arr, value: JSON.stringify(stackObj)});
}

/**
 * Removed an element from a stored array (by reference)
 *
 * @param {string} arr The array.
 * @param {string} ref The reference to remove.
 * @param {string} value The value.
 */
export const updateStoredArrayElement = async (arr, ref, value) => {
  let stack = await storage.get({key: arr});
  if (stack.value && stack.value.length > 0) {
    let stackObj = JSON.parse(stack.value);

    const i = stackObj.findIndex(update => update.ref === ref);

    if (i > -1) {
      stackObj[i].value = value;
      await storage.set({key: arr, value: JSON.stringify(stackObj)});
    }
  }
}

/**
 * Removed an element from a stored array (by reference)
 *
 * @param {string} arr The array.
 * @param {string} ref The reference to remove.
 */
export const removeStoredArrayElement = async (arr, ref) => {
  let stack = await storage.get({key: arr});
  if (stack.value && stack.value.length > 0) {
    let stackObj = JSON.parse(stack.value);

    const i = stackObj.findIndex(update => update.ref === ref);

    if (i > -1) {
      stackObj.splice(i, 1);
      await storage.set({key: arr, value: JSON.stringify(stackObj)});
    }
  }
}

/**
 * Removed an element from a stored array (by reference)
 *
 * @param {string} arr The array.
 * @param {string} ref The reference to remove.
 */
 export const cleanStoredArray = async (arr, weeks) => {
  let xWeeksAgo = moment().subtract(weeks, 'weeks');
  let haveDeleted = false;

  let stack = await storage.get({key: arr});
  if (stack.value && stack.value.length > 0) {
    let stackObj = JSON.parse(stack.value);

    for (let i = stackObj.length - 1; i >= 0; i--) {
      if (moment(stackObj[i].added) < xWeeksAgo) {
        stackObj.splice(i, 1);
        haveDeleted = true;
      }
    }

    if (haveDeleted) {
      await storage.set({key: arr, value: JSON.stringify(stackObj)});
    }
  }
}

/**
 * Get an array from the store.
 *
 * @param {store} arr
 */
export const getStoredArray = async arr => {
  let stack = await storage.get({key: arr});
  if (stack.value && stack.value.length > 0) {
    return JSON.parse(stack.value);
  }

  return [];
}

/**
 *
 * @param {string} obj
 * @param {string|number} key
 * @param {*} value
 */
export const updateStoredObjectKey = async (obj, key, value) => {
  let sObj = {};

  let s = await storage.get({key: obj});
  if (s.value && s.value.length > 0) {
    sObj = JSON.parse(s.value);
  }

  sObj[key] = {
    added: moment().format('YYYY-MM-DD HH:mm:ss'),
    value
  };

  await storage.set({key: obj, value: JSON.stringify(sObj)});
}

/**
 *
 * @param {string} obj
 * @param {string} key
 * @param {*} defaultValue
 */
export const getStoredObjectKey = async (obj, key, defaultValue = {}) => {
  let s = await storage.get({key: obj});
  if (s.value && s.value.length > 0) {
    let sObj = JSON.parse(s.value);

    if (key in sObj) {
      const value = sObj[key]['value'];

      if (value !== null) {
        return value;
      }
    }
  }

  return defaultValue;
}

/**
 *
 * @param {string} obj
 * @param {string} key
 */
export const removeStoredObjectKey = async (obj, key) => {
  let sObj = {};

  let s = await storage.get({key: obj});
  if (s.value && s.value.length > 0) {
    sObj = JSON.parse(s.value);

    if (sObj[key]) {
      delete sObj[key];
      await storage.set({key: obj, value: JSON.stringify(sObj)});
    }
  }
}

/**
 *
 * @param {string} obj
 * @param {int} weeks
 */
export const cleanStoredObject = async (obj, weeks) => {
  let xWeeksAgo = moment().subtract(weeks, 'weeks');
  let haveDeleted = false;

  let s = await storage.get({key: obj});
  if (s.value && s.value.length > 0) {
    let sObj = JSON.parse(s.value);

    for (const key of Object.keys(sObj)) {
      const added = sObj[key].added;

      if (added) {
        if (moment(added) < xWeeksAgo) {
          delete sObj[key];
          haveDeleted = true;
        }
      }
    }

    if (haveDeleted) {
      await storage.set({key: obj, value: JSON.stringify(sObj)});
    }
  }
}

export const getKey = async (key, defaultValue = {}) => {
  let s = await storage.get({key});

  if (s.value !== null) {
    if (typeof s.value === 'string' && s.value.length > 0) {
      try {
        return JSON.parse(s.value);
      } catch (e) {
        return s.value;
      }
    } else {
      return s.value;
    }
  }

  return defaultValue;
}

export const setKey = async (key, value) => {
  return await storage.set({key, value});
}

export const deleteKey = async key => {
  return await storage.remove({key});
}

export const clearStorage = async () => {
  return await storage.clear();
}

export const getKeys = async () => {
  const keys = await storage.keys();
  return await keys;
}

export const getPlan = async date => {
  return await getStoredObjectKey('plans', date, "{}");
}

export const getProperty = async id => {
  return await getStoredObjectKey('properties', id);
}

export const getPropertySecurityFeatures = async id => {
  const p = await getStoredObjectKey('properties', id);
  return p.securityfeatures;
}


export const getJob = async id => {
  return await getStoredObjectKey('jobs', id);
}
export const getLastVisitedJob = async () => {
  return await getKey('lastVisitedJob', null);
}
export const getBooking = async id => {
  return await getStoredObjectKey('bookings', id);
}
export const getBookingRooms = async id => {
  return await getStoredObjectKey('bookingRooms', id);
}
export const getPropertyRooms = async id => {
  return await getStoredObjectKey('propertyRooms', id);
}
export const getBookingNotes = async id => {
  return await getStoredObjectKey('bookingNotes', id);
}
export const getBookingCount = async hash => {
  return await getStoredObjectKey('bookingCount', hash);
}

export const getSelfEmployedJob = async id => {
  const jobs =  await getAllSelfEmployedJobs();

  for (const job of jobs) {
    if (job?.value?.id == id) {
      return job.value;
    }
  }
}

export const getAllJobs = async () => {
  return await getKey('jobs');
}
export const getAllSelfEmployedJobs = async () => {
  return await getStoredArray('selfEmployedJobs');
}

export const getBookings = async () => {
  return await getKey('bookings');
}

export const updateSelfEmployedJobStatus = async (id, status, oldStatus = null) => {
  let jobs =  await getAllSelfEmployedJobs();

  for (const jobDetails of jobs) {
    if (jobDetails?.value?.id == id) {
      await removeStoredArrayElement('selfEmployedJobs', jobDetails.ref);

      let jobCount = await getSelfEmployedJobsCount();
      await updateStoredObjectKey('selfEmployedJobsCount', jobDetails.value.status, jobCount[jobDetails.value.status].value - 1);
      await updateStoredObjectKey('selfEmployedJobsCount', status, jobCount[status].value + 1);

      return;
    }
  }

  //check if passed in status is not null, and if not update the status counts
  if (oldStatus !== null) {
    let jobCount = await getSelfEmployedJobsCount();
    await updateStoredObjectKey('selfEmployedJobsCount', oldStatus, jobCount[oldStatus].value - 1);
    await updateStoredObjectKey('selfEmployedJobsCount', status, jobCount[status].value + 1);
  }

}

export const getAllSelfEmployedInvoices = async () => {
  return await getStoredArray('selfEmployedInvoices');
}

export const getSelfEmployedJobsCount = async () => {
  return await getKey('selfEmployedJobsCount');
}

export const getBookingsCount = async () => {
  return await getKey('bookingsCount');
}

export const getSelfEmployedJobsStats = async () => {
  return await getKey('selfEmployedJobsStats');
}
export const getBookingsStats = async () => {
  return await getKey('bookingsStats');
}
export const getSelfEmployedInvoicesStats = async () => {
  return await getKey('selfEmployedInvoicesStats');
}

export const getJobNotes = async (id) => {
  const j = await getStoredObjectKey('jobs', id);

  const isArchived = (note) => note.archived && note.archived.trim() !== '';
  const jn = (j.notes || []).filter((note) => !isArchived(note.note));

  const bn = (j.bookingnotes || []).filter(
    (bookingNote) =>
      !isArchived(bookingNote.note) && bookingNote.note.visibletocleaner,
  );

  return [...jn, ...bn];
};

export const getJobFlags = async id => {
  return await getStoredObjectKey('jobFlags', id);
}

export const getJobChecklists = async id => {
  const job = await getStoredObjectKey('jobs', id);
  return job?.checklists ?? [];
}

export const getAllJobFlags = async () => {
  return await getKey('jobFlags');
}

export const removeJobFlags = async id => {
  return await updateStoredObjectKey('jobFlags', id, {});
}

export const updateJobFlags = async (id, obj) => {
  const jp = await getStoredObjectKey('jobFlags', id);
  return await updateStoredObjectKey('jobFlags', id, {...jp, ...obj});
}

export const getEstimateJobs = async () => {
  return await getKey('estimateJobs', []);
}

export const getSubmittedExpenses = async (jobId) => {
  return await getStoredObjectKey('estimatesSubmitted', jobId);
}

export const storeSubmittedExpenses = async (jobId, expenses) => {
  return await updateStoredObjectKey('estimatesSubmitted', jobId, expenses);
}

export const removeSubmittedEstimates = async jobId => {
  return await updateStoredObjectKey('estimatesSubmitted', jobId, {});
}

export const getSubmittedNotes = async jobId => {
  return await getStoredObjectKey('submittedNotes', jobId, []);
}

export const removeSubmittedNotes = async jobId => {
  return await updateStoredObjectKey('submittedNotes', jobId, []);
}

export const storeSubmittedNote = async (jobId, note) => {
  let notes = await getStoredObjectKey('submittedNotes', jobId, []);
  notes.push(note);
  return await updateStoredObjectKey('submittedNotes', jobId, notes);
}

export const getSubmittedChecklists = async jobId => {
  return await getStoredObjectKey('submittedChecklists', jobId, []);
}

export const storeSubmittedChecklists = async (jobId, checklists) => {
  return updateStoredObjectKey('submittedChecklists', jobId, checklists);
}

export const removeSubmittedChecklists = async jobId => {
  return await updateStoredObjectKey('submittedChecklists', jobId, []);
}

export const readPmsMobileSettings = async () => {
  try {
    // Ensure settings are available in the cache.
    const settingsFromCache = await getKey('settings') ?? "";
    if ("" === settingsFromCache || !settingsFromCache) {
      await storeSettings();
    }

    const settingsFromRequest = await getKey('settings');
    if ("" === settingsFromRequest || !settingsFromRequest) {
      // Can't recover, just return an empty object.
      return {};
    }

    const config = settingsFromRequest.find(setting => setting.name === 'PMS config');
    return JSON.parse(config.defaultvalue);
  } catch (e) {
    // fail silently with no data.
    return {};
  }
}

export const getBrandingIds = async () => {
  let user;
  try {
    user = JSON.parse(localStorage.apiuser);
  } catch (e) {
    console.error('Error parsing user from localStorage:', e);
    return [];
  }

  const settings = await getKey('settings');
  const parsedSettings = isArray(settings) ? settings : [];
  const config = parsedSettings?.find(setting => setting.name === 'PMS config');

  if (config) {
    const configValue = JSON.parse(config.defaultvalue);
    const group = configValue.groups.find(g => g.fieldWorkers.includes(user.id));

    if (group) {
      return group.brandingIds;
    } else {
      console.warn('No matching group found for user:', user.id);
    }
  } else {
    console.warn('No PMS config found in settings');
  }

  return [];
}

export const getAllProperties = async () => {
  return await getKey('allproperties', []);
}

export const getAllPropertiesNano = async () => {
  return await getKey('allpropertiesnano', []);
}

export const getUserAssociatedProperties = async () => {
  return await getKey('associatedproperties', []);
}

export const getFile = async fileId => {
  const keys = await getKeys();

  for (const key of keys) {
    if (key.startsWith('file_' + fileId + '_')) {
      const base64 = await getKey(key, '');
      const res = await fetch(base64);
      const blob = await res.blob();

      return blob;
    }
  }

  return '';
}

export const getFileBase64 = async fileId => {
  const keys = await getKeys();

  for (const key of keys) {
    if (key.startsWith('file_' + fileId + '_')) {
      const base64 = await getKey(key, '');
      return base64;
    }
  }

  return '';
}

export const storeFile = async (fileId, file) => {
  const base64 = await toBase64(file);

  return await setKey(
    `file_${fileId}_${moment().format('YYYYMMDD')}`,
    base64
  );
}

export const getDocument = async fileName => {
  const keys = await getKeys();

  for (const key of keys) {
    if (key.startsWith('document_' + fileName + '_')) {
      const base64 = await getKey(key, '');
      const res = await fetch(base64);
      const blob = await res.blob();

      return blob;
    }
  }

  return '';
}

export const getDocumentBase64 = async fileName => {
  const keys = await getKeys();

  for (const key of keys) {
    if (key.startsWith('document_' + fileName + '_')) {
      const base64 = await getKey(key, '');
      return base64;
    }
  }

  return '';
}

export const storeDocument = async (fileName, base64) => {
  return await setKey(
    `document_${fileName}_${moment().format('YYYYMMDD')}`,
    base64
  );
}

/**
 *
 * @param {int} weeks
 */
export const cleanStoredDocumentsAndFiles = async (weeks) => {
  const keys = await getKeys();

  let xWeeksAgo = moment().subtract(weeks, 'weeks');

  for (const key of keys) {
    if (key.startsWith('file_') || key.startsWith('document_')) {
      const dateStr = key.substr(key.lastIndexOf("_") + 1);
      const date = moment(dateStr, "YYYYMMDD");

      if (date < xWeeksAgo) {
        await storage.remove({key});
      }
    }
  }
}

const toBase64 = file => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = () => resolve(reader.result);
  reader.onerror = error => reject(error);
});
